import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import Loginimg from "../components/img/Login.svg";
import UserIcon from "../components/img/UserIcon.svg";
import LockIcon from "../components/img/LockIcon.svg";
import { useNavigate, useLocation } from "react-router-dom"; // Import useNavigate hook
import api from "../api/axiosInstance";

const Login = () => {
  const location = useLocation();
  const { from, project_type, id } = location.state || {};
  const passedPhoneNumber = location.state?.phoneNumber || "";
  const navigate = useNavigate(); // Initialize the navigate function
  const [phoneNumber, setPhoneNumber] = useState(passedPhoneNumber);
  const [password, setPassword] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [errors, setErrors] = useState({});

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const validationErrors = {};

    if (!phoneNumber.trim())
      validationErrors.phoneNumber = "Email / mobile number is required.";

    if (!password.trim()) validationErrors.password = "Password is required.";

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post("/api/login/", {
        phone_number: phoneNumber,
        password: password,
      });

      if (response.status === 200 && response.data.status === "success") {
        const { access, refresh, user_id } = response.data.data;

        localStorage.setItem("accessToken", access);
        localStorage.setItem("refreshToken", refresh);
        localStorage.setItem("user_id", user_id);

        if (project_type && id) {
          navigate("/AppPricing", { state: { project_type, id } });
        } else {
          navigate("/dashboard");
        }
      } else {
        showModal(response.data.message || "Login failed.");
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response) {
        showModal(error.response.data.message || "Invalid credentials.");
      } else {
        showModal("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const showModal = (message) => {
    setModalMessage(message);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalMessage("");
  };

  const handleForgotPassword = () => {
    navigate("/ForgotPassword"); // Navigate to the ForgotPassword page
  };

  const handleOtpLogin = () => {
    const trimmedValue = phoneNumber.trim();

    // Don't navigate if input is empty
    if (!trimmedValue) {
      showModal(
        "Please enter your email or mobile number before using OTP login."
      );
      return;
    }

    let type = "";

    // Basic phone vs email check
    if (/^\d{10,15}$/.test(trimmedValue)) {
      type = "phone";
    } else if (/^\S+@\S+\.\S+$/.test(trimmedValue)) {
      type = "email";
    } else {
      showModal("Please enter a valid phone number or email.");
      return;
    }

    navigate("/OtpNew", {
      state: {
        type,
        value: trimmedValue,
      },
    });
  };

  const handleSignUp = () => {
    navigate("/MainLogin"); // Navigate to the ForgotPassword page
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Content */}
      <div className="w-full lg:w-1/2 flex justify-center items-center p-6">
        <div className="bg-white p-6 rounded-lg w-full max-w-md">
          <h2 className="text-2xl font-bold text-center text-black mb-4 font-inter">
            Login with Password
          </h2>
          <p className="text-center mb-6 font-inter text-sm text-black-600">
            Hi! Welcome to Onzitr Marketplace
          </p>
          <div className="flex justify-center items-center">
            {" "}
            <form className="space-y-6" onSubmit={handleLogin}>
              <div>
                <label className="block text-sm mb-1 font-normal font-inter">
                  Email / mobile number
                </label>
                <div className="relative">
                  {/* Move icon to LEFT */}
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-black">
                    <img src={UserIcon} className="h-5 w-5" alt="User Icon" />
                  </span>
                  {/* Add left padding to input */}
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-2 border-2 border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB]"
                    placeholder="Email / mobile number"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value);
                      setErrors((prev) => ({ ...prev, phoneNumber: "" }));
                    }}
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.phoneNumber}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm mb-1 font-normal font-inter">
                  Password
                </label>
                <div className="relative">
                  {/* Left icon */}
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-black">
                    <img src={LockIcon} className="h-5 w-5" alt="Lock Icon" />
                  </span>

                  {/* Right eye icon */}
                  <span
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-black cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                  </span>

                  {/* Input with padding for icons */}
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full pl-10 pr-10 py-2 border-2 border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB]"
                    placeholder="Password"
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrors((prev) => ({ ...prev, password: "" }));
                    }}
                  />
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500 mt-1">{errors.password}</p>
                )}
                <div className="flex justify-end pr-4 mt-1">
                  {/* <span className="text-sm font-inter">
                    Login with{" "}
                    <span
                      class="cursor-pointer"
                      style={{ color: "#9061DB" }}
                      onClick={handleOtpLogin}
                    >
                      OTP
                    </span>
                  </span> */}

                  <p
                    className="text-sm font-inter cursor-pointer hover:underline"
                    onClick={handleForgotPassword} // Handle the click event
                  >
                    Forgot password?
                  </p>
                </div>
              </div>
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`bg-gradient-to-r from-[#4C3A68] to-[#9061DB] text-white rounded-md transition font-inter w-32 h-10 ${
                    isLoading
                      ? "opacity-60 cursor-not-allowed"
                      : "hover:opacity-90"
                  }`}
                >
                  {isLoading ? "Logging in..." : "Login Now"}
                </button>
              </div>

              <div className="flex justify-center mt-4">
                <p className="font-inter">
                  Back to{" "}
                  <span
                    class="cursor-pointer"
                    style={{ color: "#9061DB" }}
                    onClick={handleSignUp}
                  >
                    Login
                  </span>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right Content */}
      <div
        className="w-full lg:w-1/2 min-h-[300px] lg:min-h-screen bg-no-repeat bg-center bg-cover"
        style={{ backgroundImage: `url(${Loginimg})` }}
      ></div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-md max-w-sm w-full shadow-lg text-center">
            <p className="text-gray-800 mb-4">{modalMessage}</p>
            <button
              onClick={closeModal}
              className="px-4 py-2 bg-[#9061DB] text-white rounded hover:opacity-90"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
